import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Container,
  Fade,
  Grow,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

const MainLogin = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const goToLogin = (type) => {
    if (type === "shop") {
      navigate("/login/shop");
      console.log("Navigating to shop login");
    } else if (type === "dispatch") {
      navigate("/login/dispatch");
      console.log("Navigating to dispatch login");
    } else if (type === "admin") {
      navigate("/login/admin");
      console.log("Navigating to admin login");
    }
  };

  useEffect(() => {
    sessionStorage.setItem("lastLoginPath", window.location.pathname);
  }, []);

  const loginCards = [
    {
      id: "shop",
      title: "Shop Login",
      description: "Access your shopping account and manage your orders",
      icon: <StorefrontOutlinedIcon sx={{ fontSize: 40 }} />,
      image:
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=200&fit=crop&auto=format", // Modern retail storefront
      color: "#E63D24",
      lightColor: "#ffebee",
    },
    {
      id: "dispatch",
      title: "Dispatch Login",
      description: "Manage deliveries, track orders and coordinate logistics",
      icon: <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 40 }} />,
      image:
        "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=300&h=200&fit=crop&auto=format", // Warehouse/logistics center
      color: "#FF5722",
      lightColor: "#fff3e0",
    },
    {
      id: "admin",
      title: "Admin Login",
      description: "System administration and user management portal",
      icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 40 }} />,
      image:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=300&h=200&fit=crop&auto=format", // Modern office with multiple monitors
      color: "#D32F2F",
      lightColor: "#ffebee",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",

        display: "flex",
        alignItems: "center",
        padding: 2,
      }}
      className="login-page"
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={1000}>
          <Box textAlign="center" mb={6}>
            {/* Logo Section */}
            <Paper
              elevation={8}
              sx={{
                display: "inline-block",
                padding: 3,
                borderRadius: 3,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <img
                  src={require("../../assets/logo.png")}
                  alt="Logo"
                  width={200}
                />
              </Box>
            </Paper>
          </Box>
        </Fade>

        <Grid container spacing={4} justifyContent="center">
          {loginCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={card.id}>
              <Grow in={true} timeout={1000 + index * 200}>
                <Card
                  onMouseEnter={() => setHoveredCard(card.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease-in-out",
                    transform:
                      hoveredCard === card.id
                        ? "translateY(-8px)"
                        : "translateY(0)",
                    boxShadow:
                      hoveredCard === card.id
                        ? "0 20px 40px rgba(0,0,0,0.15)"
                        : "0 8px 24px rgba(0,0,0,0.1)",
                    borderRadius: 3,
                    overflow: "hidden",
                    cursor: "pointer",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                  }}
                  onClick={() => goToLogin(card.id)}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={card.image}
                    alt={card.title}
                    sx={{
                      transition: "transform 0.3s ease-in-out",
                      transform:
                        hoveredCard === card.id ? "scale(1.05)" : "scale(1)",
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, textAlign: "center", p: 3 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: card.lightColor,
                        color: card.color,
                        mb: 2,
                        transition: "all 0.3s ease-in-out",
                        transform:
                          hoveredCard === card.id ? "scale(1.1)" : "scale(1)",
                      }}
                    >
                      {card.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      component="h2"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ color: card.color }}
                    >
                      {card.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {card.description}
                    </Typography>

                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={
                        <ArrowForwardIcon
                          sx={{
                            transition: "transform 0.3s ease-in-out",
                            transform:
                              hoveredCard === card.id
                                ? "translateX(4px)"
                                : "translateX(0)",
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: card.color,
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "16px",
                        padding: "12px 24px",
                        textTransform: "none",
                        borderRadius: 2,
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          backgroundColor: card.color,
                          filter: "brightness(1.1)",
                          transform: "scale(1.02)",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToLogin(card.id);
                      }}
                    >
                      Access Portal
                    </Button>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>

        <Fade in={true} timeout={2000}>
          <Box textAlign="center" mt={6}>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                mb: 1,
              }}
            >
              Secure access to your Black Forest account
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    animation: `pulse 2s infinite ${i * 0.3}s`,
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 0.6 },
                      "50%": { opacity: 1 },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default MainLogin;
